/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\VoiceGuideSelectionModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Recording Management module.
----------------------------------------------------------------*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Volume2, Globe, AudioLines, X, Loader2, Plus, Info, ChevronLeft } from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/input';
import { useI18n } from '../../../hooks/useI18n';
import { fetchVoiceGuides, VoiceGuideAudioFile } from '../../voiceGuides/api/voiceGuidesApi';
import { VoiceGuide } from '../../voiceGuides/types';
import { VoiceLanguageSelect } from '../../../components/ui/VoiceLanguageSelect';
import { VoiceGuideFileBrowser } from '../../voiceGuides/components/VoiceGuideFileBrowser';

interface VoiceGuideSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (guide: VoiceGuide, file: VoiceGuideAudioFile) => void;
}

export function VoiceGuideSelectionModal({
  isOpen,
  onClose,
  onSelect,
}: VoiceGuideSelectionModalProps) {
  const { t } = useI18n();
  const [guides, setGuides] = useState<VoiceGuide[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  
  // Selection state
  const [selectedGuide, setSelectedGuide] = useState<VoiceGuide | null>(null);

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadGuides = useCallback(async (searchTerm: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchVoiceGuides({
        page: 1,
        limit: 100, // Load enough to browse
        sortBy: 'name',
        sortOrder: 'asc',
        search: searchTerm || undefined,
      });
      setGuides(result.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch voice guides');
      setGuides([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadGuides('');
      setSelectedGuide(null); // Reset selection when opening
      setSelectedLanguage('all');
      setSearch('');
    }
  }, [isOpen, loadGuides]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => {
        loadGuides(value);
      }, 350);
    },
    [loadGuides],
  );

  const displayGuides = guides.filter((g) => {
    if (selectedLanguage !== 'all' && selectedLanguage) {
      const guideKey = `${g.language_code}-${g.dialect_code}-${g.voice_name}`.toLowerCase();
      if (guideKey !== selectedLanguage) {
         return false;
      }
    }
    return true;
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        selectedGuide
          ? (t('recordingManagement:systemMode.selectFileModalTitle') || `Select File from ${selectedGuide.name}`)
          : (t('recordingManagement:systemMode.selectModalTitle') || 'Select PBX Voice Guide')
      }
      size="lg"
    >
      <div className="flex flex-col h-[65vh] max-h-[650px]">
        {/* Step 2: Browse Files within Selected Guide */}
        {selectedGuide ? (
          <div className="flex flex-col h-full bg-muted/10">
            <div className="p-3 border-b border-border bg-card flex items-center justify-between">
               <button
                 type="button"
                 onClick={() => setSelectedGuide(null)}
                 className="text-sm text-primary hover:underline flex items-center gap-1"
               >
                 <ChevronLeft className="h-4 w-4" />
                 {t('recordingManagement:systemMode.backToGuides') || 'Back to Guides'}
               </button>
            </div>
            
            <div className="p-4 flex-1 overflow-auto">
              {/* Reuse the existing Browser Component from VoiceGuides module */}
              <VoiceGuideFileBrowser
                 guideId={selectedGuide.id}
                 soundPath={selectedGuide.sound_path}
                 onSelectFile={(file) => onSelect(selectedGuide, file)}
              />
            </div>
          </div>
        ) : (
          /* Step 1: Browse PBX Voice Guides */
          <>
            {/* Header Filters */}
            <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-border shrink-0 bg-muted/10">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder={t('recordingManagement:systemMode.searchPlaceholder') || 'Search guides by name...'}
                  className="pl-9 bg-background"
                />
              </div>
              <div className="w-full sm:w-64">
                <VoiceLanguageSelect
                  value={selectedLanguage}
                  onChange={setSelectedLanguage}
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 relative">
              {error && (
                <div className="p-4 rounded-md bg-destructive/10 text-destructive text-sm mb-4">
                  {error}
                </div>
              )}

              {loading && displayGuides.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm z-10 text-muted-foreground gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t('common:loading') || 'Loading...'}
                </div>
              ) : displayGuides.length === 0 && !error ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-10 gap-3">
                  <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                    <AudioLines className="h-6 w-6 opacity-40" />
                  </div>
                  <p>{search ? (t('recordingManagement:systemMode.noResults') || 'No voice guides match your search.') : 'No voice guides available.'}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {displayGuides.map((guide) => (
                    <div
                      key={guide.id}
                      onClick={() => setSelectedGuide(guide)}
                      className="group border border-border rounded-lg p-3 hover:border-primary/50 hover:bg-primary/5 cursor-pointer transition-all flex flex-col gap-2 relative bg-card shadow-sm hover:shadow-md"
                    >
                      <div className="flex items-start justify-between">
                        <h4 className="font-medium text-foreground group-hover:text-primary transition-colors text-sm line-clamp-1">
                          {guide.name}
                        </h4>
                        {guide.is_system && (
                          <span className="shrink-0 rounded bg-primary/10 text-primary text-[10px] uppercase font-bold px-1.5 py-0.5 tracking-wider">
                            System
                          </span>
                        )}
                      </div>
                      
                      {guide.description && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {guide.description}
                        </p>
                      )}

                      <div className="mt-auto pt-2 flex items-center gap-3 border-t border-border/50 text-[11px] text-muted-foreground">
                        <div className="flex items-center gap-1.5" title="Language & Dialect">
                          <Globe className="h-3 w-3" />
                          <span className="uppercase">{guide.language_code}-{guide.dialect_code}</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Base Path">
                          <code className="bg-muted px-1 py-0.5 rounded text-[10px] truncate max-w-[120px]">
                            {guide.sound_path}
                          </code>
                        </div>
                      </div>

                      <div className="absolute inset-0 ring-2 ring-primary/20 opacity-0 group-hover:opacity-100 rounded-lg pointer-events-none transition-opacity" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
